import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api-client';

export interface OnboardingTrialStatus {
  trialEndsAt: string | null;
  trialExpired: boolean;
  hasActiveSubscription: boolean;
  daysRemaining: number | null;
}

export interface OnboardingStatus {
  profileComplete: boolean;
  libraryViewed: boolean;
  hasTemplate: boolean;
  hasCycle: boolean;
  hasClient: boolean;
  hasAssignedPlan: boolean;
  onboardingDismissed: boolean;
  completedCount: number;
  totalCount: number;
  trial: OnboardingTrialStatus;
}

export interface UseOnboardingChecklistResult {
  status: OnboardingStatus | null;
  loading: boolean;
  showChecklist: boolean;
  dismiss: () => Promise<void>;
  markLibraryViewed: () => Promise<void>;
  refetch: () => Promise<void>;
}

export const useOnboardingChecklist = (): UseOnboardingChecklistResult => {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  // Ref espejo del status para guards síncronos en callbacks
  // (evita stale closures sin re-crear los useCallback).
  const statusRef = useRef<OnboardingStatus | null>(null);

  const updateStatus = useCallback(
    (updater: OnboardingStatus | null | ((prev: OnboardingStatus | null) => OnboardingStatus | null)) => {
      setStatus((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (p: OnboardingStatus | null) => OnboardingStatus | null)(prev)
            : updater;
        statusRef.current = next;
        return next;
      });
    },
    []
  );

  const refetch = useCallback(async (): Promise<void> => {
    try {
      const res = await api.coach.fetchOnboardingStatus();
      updateStatus(res.data ?? (res as unknown as OnboardingStatus));
    } catch {
      // El dashboard no debería romperse si este endpoint falla.
      updateStatus(null);
    } finally {
      setLoading(false);
    }
  }, [updateStatus]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const dismiss = useCallback(async (): Promise<void> => {
    // Optimista; revertir si falla.
    updateStatus((s) => (s ? { ...s, onboardingDismissed: true } : s));
    try {
      await api.coach.markOnboarding('dismiss');
    } catch {
      updateStatus((s) => (s ? { ...s, onboardingDismissed: false } : s));
    }
  }, [updateStatus]);

  const markLibraryViewed = useCallback(async (): Promise<void> => {
    if (statusRef.current?.libraryViewed) return;
    updateStatus((s) =>
      s
        ? {
            ...s,
            libraryViewed: true,
            completedCount: s.completedCount + 1
          }
        : s
    );
    try {
      await api.coach.markOnboarding('library_viewed');
    } catch {
      updateStatus((s) =>
        s
          ? {
              ...s,
              libraryViewed: false,
              completedCount: Math.max(0, s.completedCount - 1)
            }
          : s
      );
    }
  }, [updateStatus]);

  const showChecklist = !!status && !status.onboardingDismissed && status.completedCount < status.totalCount;

  return { status, loading, showChecklist, dismiss, markLibraryViewed, refetch };
};
